/**
 * Helpers extracted from the `getResults` store action in
 * SearchResults/index.jsx (TD-2377 — SonarQube cognitive-complexity cleanup).
 *
 * `getResults` orchestrates three request shapes — a normal search, a profiler
 * poll loop, and a bulk finalized-set load — each with an onSuccess/onError body
 * that mutates the view `state`. Keeping those bodies inline pushed the action
 * (and its main onSuccess handler) over SonarQube's cognitive-complexity
 * threshold of 15. The branch-heavy state-mutation logic lives here instead, as
 * small functions that take the view `state` plus the stores/callbacks they need
 * via a `ctx` object. The async plumbing (makeRequest / setInterval) stays in the
 * action; behaviour is unchanged.
 *
 * Mirrors the in-file `getProfilerMapFields` / `sliceResultsForDemo` precedent,
 * but as a sibling module so the mutation logic is directly unit-testable
 * (the store itself has no unit harness).
 */
import { toJS } from 'mobx';
import { API_ENDPOINTS } from '../../../api/endpoints';
import { cancelRequest } from '../../../api/api';
import { mapData, mapCompanyEntry } from '../../../api/dataMappers';
import { hideBulkMatchBadgeRows } from '../../../utils/profiler/hideBulkMatchBadge';
import { normalizeOwnershipRequest } from '../../../utils/ownershipCriteria';
import { UI_OPTIONS } from '../../../utils/constants/uiOptions';
import { UI_OPTION_KEYS } from '../../../utils/constants/uiOptionKeys';
import {
  SEARCH_FIELDS,
  PORTFOLIO_FIELDS,
  FUND_FIELDS,
  INVEST_PROFILE_FIELDS,
  SEARCH_FIELDS_PORTFOLIO_INVESTMENT_CRITERIA,
  PROFILER_SEARCH_FIELDS,
  PROFILER_INDUSTRY_CODES_SEARCH_FIELDS
} from '../../../utils/constants/fields';
import { eventSearchResultsDisplayed } from '../../../ga4/ga4';
import { readableProfilerSearchType } from '../../../ga4/searchEventParams';

export const PAGE_SIZE = 50;

// Resolve the field-mapping config for a (non-profiler) search type. Extracted from
// the search onSuccess handler to keep its cognitive complexity in check (TD-2320).
export const getProfilerMapFields = (searchType) => {
  const type = searchType?.[0];
  const searchTypeOptions = UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE];
  if (type === searchTypeOptions.Funds) return FUND_FIELDS;
  if (type === searchTypeOptions['Portfolio Co.']) return PORTFOLIO_FIELDS;
  if (type === searchTypeOptions.Corporates) return INVEST_PROFILE_FIELDS;
  return {};
};

// Demo accounts only ever see the first page of results.
export const sliceResultsForDemo = (results, isProfilerDemo) =>
  isProfilerDemo ? results.slice(0, PAGE_SIZE) : results;

// Deep copy of a search-fields bag. `toJS` first: a retry passes the fields back out of the
// observable `state.failedReqParams`, and structuredClone cannot clone MobX proxies.
const cloneSearchFields = (searchFields) => structuredClone(toJS(searchFields));

// Deep-clone the [searchFields, searchId, searchCategory] tuple stored on
// `state.failedReqParams` so a later "retry" re-runs against a stable snapshot,
// decoupled from any subsequent mutation of the live search fields.
export const snapshotReqParams = (searchFields, searchId, searchCategory) => [
  cloneSearchFields(searchFields),
  searchId,
  searchCategory
];

/**
 * Map the free-text city/zipcode + distance inputs onto `bodyObj.geoLocation`,
 * enforcing the cross-field rules (radius requires a search term, radius range,
 * min city-name length). On a rule violation it records the error + a retry
 * snapshot on `viewState` and returns false; otherwise it mutates `bodyObj`
 * (adding `geoLocation`, deleting the raw fields) and returns true.
 */
export const applyGeoLocationOrSetError = (
  bodyObj,
  viewState,
  originalSearchFields,
  originalSearchId,
  originalSearchCategory
) => {
  const searchTermRaw = typeof bodyObj.cityZipcode === 'string' ? bodyObj.cityZipcode.trim() : '';
  const radiusRaw = bodyObj.distance;

  const hasRadius = typeof radiusRaw === 'number';
  const hasSearchTerm = !!searchTermRaw;

  const setError = (message) => {
    viewState.resultsError = message;
    viewState.failedReqParams = snapshotReqParams(
      originalSearchFields,
      originalSearchId,
      originalSearchCategory
    );
    viewState.isLoading = false;
    return false;
  };

  if (hasRadius && !hasSearchTerm) {
    return setError('Please provide City/Zipcode when Distance is set');
  }

  if (hasRadius && (!Number.isInteger(radiusRaw) || radiusRaw < 1 || radiusRaw > 200)) {
    return setError('Distance must be a whole number between 1 and 200 km');
  }

  if (hasSearchTerm && searchTermRaw.length < 2) {
    return setError('City name must be at least 2 characters or a 5-digit postal code');
  }

  if (hasSearchTerm) {
    // Normalize display labels like "City, State, Country" back to a search key
    // Postal codes (5 digits) are passed through unchanged
    let normalized = searchTermRaw;
    if (!/^\d{5}$/.test(normalized)) {
      normalized = normalized.split(',')[0].trim();
    }
    const geoLocation = { searchTerm: normalized, searchType: 'auto' };
    if (hasRadius) {
      geoLocation.radiusKm = radiusRaw;
    }
    bodyObj.geoLocation = geoLocation;
  }

  delete bodyObj.cityZipcode;
  delete bodyObj.distance;
  return true;
};

/**
 * Build the `makeRequest` body for the immediate (non-bulk) search dispatch:
 * profiler characteristics / industry-codes searches (mapping fields + applying
 * the geo-location cross-field rules), and fund / portfolio investment-criteria
 * searches. Returns `{ body, aborted }` — `aborted` is true when geo validation
 * failed (the error is already recorded on `state`), and the caller must skip
 * the request. Profiler-hydration side effects stay in the store action.
 */
export const buildSearchRequestBody = ({
  state,
  searchFields,
  searchId,
  searchCategory,
  isProfilerSearch,
  isProfilerCompanyCriteriaSearch,
  isProfilerIndustryCodesSearch
}) => {
  const loadingType = state.loadingSearchType[0];
  const searchTypeOptions = UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE];
  const fundCategories = UI_OPTIONS[UI_OPTION_KEYS.FUND_SEARCH_CATEGORY];
  const portfolioCategories = UI_OPTIONS[UI_OPTION_KEYS.PORTFOLIO_SEARCH_CATEGORY];

  if (isProfilerSearch) {
    if (isProfilerCompanyCriteriaSearch || isProfilerIndustryCodesSearch) {
      const profilerFields = isProfilerCompanyCriteriaSearch
        ? PROFILER_SEARCH_FIELDS
        : PROFILER_INDUSTRY_CODES_SEARCH_FIELDS;
      const body = normalizeOwnershipRequest({ ...mapData(searchFields, profilerFields, true) });
      if (!applyGeoLocationOrSetError(body, state, searchFields, searchId, searchCategory)) {
        return { body, aborted: true };
      }
      body.isReExecute = state.isReExecute;
      return { body, aborted: false };
    }
    return { body: { isReExecute: state.isReExecute }, aborted: false };
  }

  if (
    loadingType === searchTypeOptions.Funds &&
    searchCategory === fundCategories['Investment criteria']
  ) {
    return { body: { ...mapData(searchFields, SEARCH_FIELDS, true) }, aborted: false };
  }

  if (
    loadingType === searchTypeOptions['Portfolio Co.'] &&
    searchCategory === portfolioCategories['Investment criteria']
  ) {
    return {
      body: { ...mapData(searchFields, SEARCH_FIELDS_PORTFOLIO_INVESTMENT_CRITERIA, true) },
      aborted: false
    };
  }

  return { body: {}, aborted: false };
};

const scrollToTopSoon = () => {
  setTimeout(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, 0);
};

/**
 * Poll response handler for a profiler-criteria search (POLL_PROFILER_RESULTS).
 * Terminates the poll on `completed`/`failed`, no-ops on `pending`.
 */
export const handlePollResponse = (pollResponse, ctx) => {
  const {
    state,
    profilerAccessStore,
    authStore,
    searchFields,
    searchId,
    searchCategory,
    newSearchId,
    enableProfilerGrouping
  } = ctx;

  if (!state.isRendered) {
    clearInterval(state.profilerPollInterval);
    if (profilerAccessStore.isProfilerDemo) {
      authStore.refreshProfilerDemoCounter();
    }
    return;
  }

  if (pollResponse.status === 'completed') {
    clearInterval(state.profilerPollInterval);
    cancelRequest(API_ENDPOINTS.POLL_PROFILER_RESULTS);
    state.currentSearch = cloneSearchFields(searchFields);
    state.currentSearchCategory = searchCategory;
    state.searchType = searchFields[SEARCH_FIELDS.SEARCH_TYPE.NAME];
    state.failedReqParams = null;
    const polledResults = Array.isArray(pollResponse.results) ? pollResponse.results : [];
    state.results = sliceResultsForDemo(polledResults, profilerAccessStore.isProfilerDemo);
    state.fullResultsCount = pollResponse.totalCount || polledResults.length;
    state.searchId = newSearchId;
    state.isReExecute = false;
    state.isLoading = false;
    eventSearchResultsDisplayed({
      search_profile: 'corporates',
      search_type: readableProfilerSearchType(state.searchCategory),
      result_count: state.fullResultsCount,
      has_results: state.fullResultsCount > 0
    });
    if (state.isInitialLoading) {
      state.isInitialLoading = false;
    }
    // TD-2376: reconcile the demo counter from the BE on successful completion
    // (not just on error/unmount). The optimistic += 1 above can drift from the
    // authoritative count (re-execute / cache edge cases); this refetch keeps it
    // live instead of jumping on the next F5.
    // Follow-up (TD-2376, to discuss): this also fires for cached results, which
    // consumed no credit — a harmless extra GET_USER_INFO. Gating on the cached
    // flag is a possible optimization to revisit.
    if (profilerAccessStore.isProfilerDemo) {
      authStore.refreshProfilerDemoCounter();
    }
    enableProfilerGrouping();
    scrollToTopSoon();
  } else if (pollResponse.status === 'pending') {
    // do nothing — keep polling
  } else if (pollResponse.status === 'failed') {
    clearInterval(state.profilerPollInterval);
    cancelRequest(API_ENDPOINTS.POLL_PROFILER_RESULTS);
    state.resultsError = 'Failed to obtain search results';
    state.failedReqParams = snapshotReqParams(searchFields, searchId, searchCategory);
    state.isLoading = false;
    if (state.isInitialLoading) {
      state.isInitialLoading = false;
    }
    scrollToTopSoon();
  }
};

/** onError for the profiler poll request. */
export const handlePollError = (pollErrorMessage, ctx) => {
  const { state, profilerAccessStore, authStore, searchFields, searchId, searchCategory } = ctx;

  clearInterval(state.profilerPollInterval);
  if (profilerAccessStore.isProfilerDemo) {
    authStore.refreshProfilerDemoCounter();
  }

  if (!state.isRendered) {
    return;
  }

  state.resultsError = pollErrorMessage || 'Failed to obtain search results';
  state.failedReqParams = snapshotReqParams(searchFields, searchId, searchCategory);
  state.isLoading = false;
  if (state.isInitialLoading) {
    state.isInitialLoading = false;
  }
  scrollToTopSoon();
};

/**
 * onSuccess for a bulk finalized-set load (TRIGGER_PROFILER_SEARCH with a bare
 * `{ searchId }` body). The BE returns rows in the raw profiler wire shape, so
 * each row is flattened via `mapCompanyEntry` before render (React #31 guard),
 * then stripped of the AI match badge (a bulk score is the name matcher's
 * similarity, not a relevance judgement).
 */
export const handleBulkTriggerSuccess = (payload, ctx) => {
  const { state, profilerAccessStore, searchCategory, fallbackSearchId, enableProfilerGrouping } =
    ctx;

  if (!state.isRendered) {
    return;
  }

  const { results = [], totalCount = 0, searchId: newSearchId } = payload;
  const safeResults = Array.isArray(results) ? results : [];
  safeResults.forEach(mapCompanyEntry);
  hideBulkMatchBadgeRows(safeResults);
  state.currentSearch = {};
  state.currentSearchCategory = searchCategory;
  state.searchType = [UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler];
  state.failedReqParams = null;
  state.results = sliceResultsForDemo(safeResults, profilerAccessStore.isProfilerDemo);
  state.fullResultsCount = totalCount || safeResults.length;
  state.searchId = newSearchId || fallbackSearchId;
  state.isReExecute = false;
  eventSearchResultsDisplayed({
    search_profile: 'corporates',
    search_type: readableProfilerSearchType(searchCategory),
    result_count: state.fullResultsCount,
    has_results: state.fullResultsCount > 0
  });
  enableProfilerGrouping();
};

/**
 * onSuccess for the immediate (non-poll) branch of a search: profiler industry
 * codes, fund and portfolio searches, plus profiler-criteria responses already
 * cached by the BE. Slices/maps results and increments the demo executed count
 * for a fresh (non-cached) profiler search.
 */
export const applyDirectSearchResults = (payload, ctx) => {
  const {
    state,
    isProfilerSearch,
    profilerAccessStore,
    authStore,
    searchFields,
    searchCategory,
    body,
    enableProfilerGrouping
  } = ctx;
  const { results = [], newSearchId, totalCount = 0, isCached = false } = payload;

  state.currentSearch = cloneSearchFields(searchFields);
  state.currentSearchCategory = searchCategory;
  state.searchType = searchFields[SEARCH_FIELDS.SEARCH_TYPE.NAME];
  state.failedReqParams = null;

  const mapFields = getProfilerMapFields(state.searchType);

  // Normalize null/non-array results to [] for parity with the poll path: a
  // { results: null } empty response would otherwise crash sliceResultsForDemo /
  // mapData and state.results.length downstream.
  const safeResults = Array.isArray(results) ? results : [];

  state.results = isProfilerSearch
    ? sliceResultsForDemo(safeResults, profilerAccessStore.isProfilerDemo)
    : mapData(safeResults, mapFields);

  state.fullResultsCount = totalCount || safeResults.length;
  state.searchId = newSearchId;
  state.isReExecute = false;
  eventSearchResultsDisplayed({
    search_profile: isProfilerSearch ? 'corporates' : state.searchType?.[0] || '',
    search_type: isProfilerSearch
      ? readableProfilerSearchType(state.searchCategory)
      : state.currentSearchCategory || '',
    result_count: state.fullResultsCount,
    has_results: state.fullResultsCount > 0
  });

  if (
    isProfilerSearch &&
    profilerAccessStore.isProfilerDemo &&
    !isCached &&
    body.searchId !== newSearchId
  ) {
    authStore.userInfo.profilerSearchExecutedCount += 1;
    // TD-2376: reconcile from the BE right after consuming a credit so the demo
    // counter is authoritative without waiting for an F5. Kept inside this branch
    // (only when a credit was actually consumed) so it adds no branching to this
    // already-large handler.
    authStore.refreshProfilerDemoCounter();
  }

  if (isProfilerSearch) {
    enableProfilerGrouping();
  }
};

/**
 * Arms the profiler-criteria poll loop.
 *
 * Lives here rather than inline in `getResults` because building the poller
 * inside that action's `onSuccess` put the `makeRequest` callbacks six closures
 * deep (SonarCloud S2004, limit 5). `immediate` reproduces the original branch:
 * when the response already carries the search id the first poll fires straight
 * away, otherwise the loop is driven by a 5s interval.
 */
export const startProfilerPoll = ({ makeRequest, newSearchId, immediate, pollCtx }) => {
  const poll = () =>
    makeRequest({
      endpoint: API_ENDPOINTS.POLL_PROFILER_RESULTS,
      body: { searchId: newSearchId },
      onSuccess: (pollResponse) => handlePollResponse(pollResponse, pollCtx),
      onError: (pollErrorMessage) => handlePollError(pollErrorMessage, pollCtx)
    });

  if (immediate) {
    poll();
    return;
  }

  pollCtx.state.profilerPollInterval = setInterval(poll, 5_000);
};

/**
 * Flips `isExcluded` on the single loaded result matching `entryId`.
 * Extracted so the lookup callback does not sit inside the exclude request's
 * `onSuccess`, which is already four closures deep.
 */
export const markResultExcluded = (state, entryId, exclude) => {
  const target = state.results?.find?.((entry) => entry.id === entryId);
  if (target) {
    target.isExcluded = exclude;
  }
};

/**
 * Whether the results-list pagination effect may run.
 *
 * A bulk upload search loads its finalized set by `searchId` alone and keeps
 * `currentSearch` empty, so the criteria check below never passed for it and the
 * list stayed on a single 50-row page. A bulk search paginates once its set has
 * loaded (`searchId` set, no error, so a failed load keeps its own error message);
 * every other search keeps the original rule. Compared against the literal token,
 * like the rest of SearchResults, so recognising bulk never depends on the
 * picker's option set.
 *
 * @param {object} state SearchResults view state.
 * @param {string|null} searchCategory The URL's `searchCategory`.
 * @returns {boolean}
 */
export const canPaginateResults = (state, searchCategory) => {
  if (searchCategory === 'companyBulkSearch') {
    return !state.resultsError && !!state.searchId;
  }
  return Object.values(state.currentSearch || {}).length > 0;
};

/**
 * Bulk counterpart of `markResultExcluded` — marks every loaded result whose id
 * is in `companyIds` as excluded. Caller owns the `runInAction` wrapper.
 */
export const markResultsExcluded = (state, companyIds) => {
  const idSet = new Set(companyIds);
  state.results.forEach((entry) => {
    if (idSet.has(entry.id)) {
      entry.isExcluded = true;
    }
  });
};
